<template>
  <div class="report-overlay">
    <div class="report-card">
      <div class="report-title">
        <svg class="report-siren" viewBox="0 0 32 32" aria-hidden="true">
          <path d="M8 22a8 8 0 0 1 16 0z" fill="#1d4fb8" />
          <rect x="5" y="22" width="22" height="5" rx="1.5" fill="#1d4fb8" />
          <path
            d="M16 4v4M5 9l3 3M27 9l-3 3"
            stroke="#1d4fb8"
            stroke-width="2.5"
            stroke-linecap="round"
          />
        </svg>
        오류신고
      </div>

      <div v-if="!submitted">
        <p class="report-subtitle">
          로그인에 문제가 있나요? 아래 내용을 알려주세요.
        </p>

        <div class="report-row">
          <label class="report-label">사용자 유형</label>
          <div class="report-field">
            <select v-model="form.userType" class="report-input">
              <option v-for="t in USER_TYPES" :key="t.value" :value="t.value">
                {{ t.label }}
              </option>
            </select>
          </div>
        </div>

        <div class="report-row">
          <label class="report-label">문의 유형</label>
          <div class="report-field">
            <select v-model="form.inquiryType" class="report-input">
              <option value="" disabled>문의 유형 선택</option>
              <option
                v-for="t in INQUIRY_TYPES"
                :key="t.value"
                :value="t.value"
              >
                {{ t.label }}
              </option>
            </select>
            <p v-if="visibleErrors.inquiryType" class="report-error">
              {{ visibleErrors.inquiryType }}
            </p>
          </div>
        </div>

        <div class="report-row">
          <label class="report-label">{{ schoolLabel }}</label>
          <div class="report-field">
            <div class="school-search">
              <input
                v-model="schoolKeyword"
                placeholder="학교명을 검색해주세요."
                class="report-input"
                @input="onSchoolInput"
                @focus="schoolListOpen = true"
                @blur="schoolListOpen = false"
              />
              <ul v-if="schoolListOpen && !form.school" class="school-list">
                <li v-if="searching" class="school-item muted">검색 중...</li>
                <li
                  v-for="school in schoolResults"
                  :key="school.id"
                  class="school-item"
                  @mousedown.prevent="selectSchool(school)"
                >
                  {{ school.name }}
                </li>
                <li
                  class="school-item unknown"
                  @mousedown.prevent="selectSchool(UNKNOWN_SCHOOL)"
                >
                  {{ UNKNOWN_SCHOOL.name }}
                </li>
              </ul>
            </div>
            <p v-if="visibleErrors.school" class="report-error">
              {{ visibleErrors.school }}
            </p>
          </div>
        </div>

        <div v-if="needsGradeClass" class="report-row">
          <label class="report-label">{{ gradeClassLabel }}</label>
          <div class="report-field">
            <div class="report-split">
              <select
                v-model="form.grade"
                :disabled="!schoolKnown"
                class="report-input"
              >
                <option value="" disabled>학년 선택</option>
                <option v-for="g in GRADES" :key="g" :value="g">
                  {{ g }}학년
                </option>
              </select>
              <select
                v-model="form.classNo"
                :disabled="!schoolKnown || !form.grade"
                class="report-input"
              >
                <option value="" disabled>반 선택</option>
                <option v-for="c in CLASSES" :key="c" :value="c">
                  {{ c }}반
                </option>
              </select>
            </div>
            <p v-if="visibleErrors.gradeClass" class="report-error">
              {{ visibleErrors.gradeClass }}
            </p>
          </div>
        </div>

        <div class="report-row">
          <label class="report-label">아이디 (선택)</label>
          <div class="report-field">
            <input
              v-model="form.loginId"
              placeholder="로그인할 때 입력한 아이디"
              class="report-input"
            />
          </div>
        </div>

        <div v-if="needsEmail" class="report-row">
          <label class="report-label">회신 이메일</label>
          <div class="report-field">
            <input
              v-model="form.email"
              type="email"
              placeholder="답변 받을 이메일 (선택)"
              class="report-input"
            />
            <p v-if="visibleErrors.email" class="report-error">
              {{ visibleErrors.email }}
            </p>
          </div>
        </div>

        <div class="report-row">
          <label class="report-label">내용</label>
          <div class="report-field">
            <textarea
              v-model="form.content"
              :maxlength="CONTENT_MAX_LENGTH"
              placeholder="어떤 문제가 있었는지 20자 이상 알려주세요."
              class="report-input report-textarea"
            ></textarea>
            <p class="report-hint">
              <span>비밀번호는 적지 마세요.</span>
              <span>{{ contentLength }}/{{ CONTENT_MAX_LENGTH }}</span>
            </p>
            <p v-if="visibleErrors.content" class="report-error">
              {{ visibleErrors.content }}
            </p>
          </div>
        </div>

        <p class="report-notice">
          오류 확인을 위해 기기·접속 정보가 함께 전송돼요.<br />
          입력한 정보는 오류 확인과 답변에 사용돼요.
        </p>
        <p v-if="submitError" class="report-error center">
          {{ submitError }}
        </p>

        <div class="report-buttons">
          <button
            type="button"
            class="report-btn back"
            :disabled="sending"
            @click="$emit('close')"
          >
            뒤로가기
          </button>
          <button
            type="button"
            class="report-btn send"
            :disabled="sending"
            @click="submit()"
          >
            {{ sending ? "보내는 중" : "보내기" }}
          </button>
        </div>
      </div>

      <div v-else class="report-done">
        <p>오류 신고가 접수되었어요.</p>
        <p>확인 후 빠르게 도와드릴게요.</p>
        <div class="report-buttons">
          <button type="button" class="report-btn send" @click="$emit('close')">
            확인
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { searchSchools, submitLoginErrorReport } from "@/api.js";
import { collectEnvInfo } from "@/utils/envCollector";
import {
  USER_TYPES,
  INQUIRY_TYPES,
  GRADES,
  CLASSES,
  UNKNOWN_SCHOOL,
  CONTENT_MIN_LENGTH,
  CONTENT_MAX_LENGTH,
} from "./errorReportOptions";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SEARCH_DEBOUNCE_MS = 300;

// 서버가 접수를 거절한 사유별 안내
const SUBMIT_ERROR_MESSAGES = {
  too_many_requests:
    "오류 신고는 1시간에 최대 5회까지 가능합니다. 잠시 후 다시 시도해 주세요.",
  bad_word: "내용에 사용할 수 없는 단어가 있어요. 수정 후 다시 보내주세요.",
  invalid_content: `문의 내용을 ${CONTENT_MIN_LENGTH}자 이상 적어주세요.`,
};
const DEFAULT_SUBMIT_ERROR = "전송에 실패했어요. 잠시 후 다시 보내주세요.";

export default {
  props: {
    initialLoginId: {
      type: String,
      default: "",
    },
    // 신고 시점의 WebGL 로딩 상태 등 화면 쪽 정보
    envExtra: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      USER_TYPES,
      INQUIRY_TYPES,
      GRADES,
      CLASSES,
      UNKNOWN_SCHOOL,
      CONTENT_MAX_LENGTH,
      form: {
        userType: "student",
        inquiryType: "",
        school: null,
        grade: "",
        classNo: "",
        loginId: this.initialLoginId,
        email: "",
        content: "",
      },
      schoolKeyword: "",
      schoolResults: [],
      schoolListOpen: false,
      searching: false,
      searchTimer: null,
      searchSeq: 0,
      showErrors: false,
      sending: false,
      submitError: "",
      submitted: false,
    };
  },
  computed: {
    needsGradeClass() {
      return this.form.userType !== "teacher";
    },
    needsEmail() {
      return this.form.userType !== "student";
    },
    schoolLabel() {
      return this.form.userType === "parent" ? "자녀 학교" : "학교";
    },
    gradeClassLabel() {
      return this.form.userType === "parent" ? "자녀 학년·반" : "학년·반";
    },
    schoolKnown() {
      return !!this.form.school && this.form.school.id !== UNKNOWN_SCHOOL.id;
    },
    contentLength() {
      return this.form.content.trim().length;
    },
    fieldErrors() {
      const errors = {};
      const email = this.form.email.trim();

      if (!this.form.inquiryType) {
        errors.inquiryType = "문의 유형을 선택해주세요.";
      }
      if (!this.form.school) {
        errors.school = "학교를 검색해서 선택해주세요.";
      }
      if (
        this.needsGradeClass &&
        this.schoolKnown &&
        (!this.form.grade || !this.form.classNo)
      ) {
        errors.gradeClass = "학년과 반을 선택해주세요.";
      }
      if (this.needsEmail && email && !EMAIL_PATTERN.test(email)) {
        errors.email = "이메일 형식을 확인해주세요.";
      }
      if (this.contentLength < CONTENT_MIN_LENGTH) {
        errors.content = `문의 내용을 ${CONTENT_MIN_LENGTH}자 이상 적어주세요. (현재 ${this.contentLength}자)`;
      }
      return errors;
    },
    visibleErrors() {
      return this.showErrors ? this.fieldErrors : {};
    },
  },
  watch: {
    "form.grade"() {
      this.form.classNo = "";
    },
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    resetSchool() {
      this.form.school = null;
      this.form.grade = "";
      this.form.classNo = "";
    },
    onSchoolInput() {
      this.resetSchool();
      this.schoolListOpen = true;
      clearTimeout(this.searchTimer);

      const keyword = this.schoolKeyword.trim();
      if (!keyword) {
        this.schoolResults = [];
        this.searching = false;
        return;
      }

      this.searching = true;
      this.searchTimer = setTimeout(
        () => this.search(keyword),
        SEARCH_DEBOUNCE_MS
      );
    },
    async search(keyword) {
      const seq = ++this.searchSeq;
      try {
        const results = await searchSchools(keyword);
        if (seq === this.searchSeq) this.schoolResults = results;
      } catch {
        if (seq === this.searchSeq) this.schoolResults = [];
      } finally {
        if (seq === this.searchSeq) this.searching = false;
      }
    },
    selectSchool(school) {
      this.resetSchool();
      this.form.school = school;
      this.schoolKeyword = school.name;
      this.schoolListOpen = false;
    },
    // 사용자 유형에서 숨겨진 항목은 전송하지 않는다
    async buildPayload() {
      const { form } = this;
      const payload = {
        userType: form.userType,
        inquiryType: form.inquiryType,
        school: {
          id: this.schoolKnown ? form.school.id : null,
          name: this.schoolKnown ? form.school.name : null,
          unknown: !this.schoolKnown,
        },
        loginId: form.loginId.trim() || null,
        content: form.content.trim(),
        env: await collectEnvInfo(this.envExtra),
      };

      if (this.needsGradeClass && this.schoolKnown) {
        payload.grade = form.grade;
        payload.classNo = form.classNo;
      }
      if (this.needsEmail && form.email.trim()) {
        payload.email = form.email.trim();
      }
      return payload;
    },
    async submit() {
      if (this.sending) return;

      this.showErrors = true;
      this.submitError = "";
      if (Object.keys(this.fieldErrors).length > 0) return;

      this.sending = true;
      try {
        await submitLoginErrorReport(await this.buildPayload());
        this.submitted = true;
      } catch (err) {
        this.submitError =
          SUBMIT_ERROR_MESSAGES[err.reason] || DEFAULT_SUBMIT_ERROR;
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style scoped>
.report-overlay {
  position: fixed;
  inset: 0;
  z-index: 100001;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.45);
  padding: 16px;
}

.report-card {
  position: relative;
  width: 640px;
  max-width: 100%;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  padding: 64px 36px 28px;
  border-radius: 28px;
  background-color: #ffffff;
  box-shadow: 0 8px 0 #b9dcf7, 0 20px 40px rgba(0, 0, 0, 0.25);
  font-family: notosansmedium, sans-serif;
  color: #333333;
}

.report-title {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 44px;
  border-radius: 0 0 10px 10px;
  background: linear-gradient(#6fd6fa, #3bbcf0);
  color: #12307a;
  font-family: cookiebold, sans-serif;
  font-size: 30px;
  white-space: nowrap;
}

.report-siren {
  width: 30px;
  height: 30px;
}

.report-subtitle {
  margin: 0 0 18px;
  text-align: center;
  color: #1d4fb8;
  font-size: 15px;
}

.report-row {
  display: grid;
  grid-template-columns: 110px 1fr;
  align-items: start;
  gap: 8px;
  margin-bottom: 10px;
}

.report-label {
  padding-top: 9px;
  font-family: notosans, sans-serif;
  font-size: 14px;
}

.report-field {
  min-width: 0;
}

.report-split {
  display: flex;
  gap: 8px;
}

.report-split > * {
  flex: 1;
  min-width: 0;
}

.report-input {
  width: 100%;
  height: 38px;
  padding: 0 14px;
  border: none;
  border-bottom: 3px solid #a9d4f5;
  border-radius: 10px;
  outline: none;
  background-color: #e3eaf8;
  color: #333333;
  font-size: 14px;
}

.report-input:focus {
  border-bottom-color: #3bbcf0;
}

.report-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.report-input::placeholder {
  color: #7a849a;
}

select.report-input {
  appearance: none;
  padding-right: 34px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 10'%3E%3Cpath d='M0 0h12L6 10z' fill='%231d4fb8'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 12px;
  cursor: pointer;
}

.report-textarea {
  height: 96px;
  padding: 10px 14px;
  resize: none;
}

.report-hint {
  display: flex;
  justify-content: space-between;
  margin: 4px 2px 0;
  color: #7a849a;
  font-size: 12px;
}

.report-error {
  margin: 4px 2px 0;
  color: #e0303a;
  font-size: 12px;
}

.report-error.center {
  text-align: center;
}

.school-search {
  position: relative;
}

.school-list {
  position: absolute;
  top: 42px;
  left: 0;
  right: 0;
  z-index: 2;
  max-height: 200px;
  overflow-y: auto;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  border-radius: 10px;
  background-color: #ffffff;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2);
}

.school-item {
  padding: 8px 14px;
  font-size: 14px;
  cursor: pointer;
}

.school-item:hover {
  background-color: #e3eaf8;
}

.school-item.muted {
  color: #7a849a;
  cursor: default;
}

.school-item.unknown {
  border-top: 1px solid #e3eaf8;
  color: #1d4fb8;
}

.report-notice {
  margin: 14px 0 0;
  text-align: center;
  color: #7a849a;
  font-size: 12px;
  line-height: 1.6;
}

.report-buttons {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 18px;
}

.report-btn {
  width: 164px;
  height: 63px;
  padding-bottom: 6px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100% 100%;
  color: #ffffff;
  font-family: cookiebold, sans-serif;
  font-size: 24px;
  /* 글씨 테두리 */
  text-shadow:
    -1px -1px 0 var(--btn-outline),
    0 -1px 0 var(--btn-outline),
    1px -1px 0 var(--btn-outline),
    1px 0 0 var(--btn-outline),
    1px 1px 0 var(--btn-outline),
    0 1px 0 var(--btn-outline),
    -1px 1px 0 var(--btn-outline),
    -1px 0 0 var(--btn-outline),
    0 2px 0 var(--btn-outline);
  cursor: pointer;
}

.report-btn.back {
  --btn-outline: #c2389f;
  background-image: url("../assets/Btn_Report_Pink.png");
}

.report-btn.send {
  --btn-outline: #0b6fa8;
  background-image: url("../assets/Btn_Report_Blue.png");
}

.report-btn:hover:not(:disabled) {
  filter: brightness(0.95);
}

.report-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.report-done {
  padding: 24px 0 8px;
  text-align: center;
  font-size: 18px;
}

.report-done p {
  margin: 0 0 6px;
}
</style>
